<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '../services/api'
import LanguageSelector from '../components/LanguageSelector.vue'

const { t } = useI18n()

// Form state
const email = ref('')

// UI state
const loading = ref(false)
const error = ref<string | null>(null)
const successMessage = ref<string | null>(null)
const validationErrors = ref<Record<string, string[]>>({})

/**
 * Handle forgot password form submission
 */
const handleForgotPassword = async () => {
  // Reset messages
  error.value = null
  successMessage.value = null
  validationErrors.value = {}
  loading.value = true

  try {
    // TODO: Create forgot-password endpoint in auth.service.ts
    const response = await api.post('v1/auth/forgot-password', {
      email: email.value
    })

    successMessage.value = response.data.message || t('forgotPassword.successMessage')

    // Clear form
    email.value = ''
  } catch (err: any) {
    console.error('Forgot password error:', err)

    if (err.response?.data) {
      const apiError = err.response.data

      // Handle validation errors
      if (apiError.errors) {
        validationErrors.value = apiError.errors
      }

      // Handle general error message
      if (apiError.message) {
        error.value = apiError.message
      }
    } else {
      error.value = t('forgotPassword.errorSending')
    }
  } finally {
    loading.value = false
  }
}

/**
 * Get validation error for field
 */
const getFieldError = (field: string): string | null => {
  return validationErrors.value[field]?.[0] || null
}
</script>

<template>
  <main class="min-h-screen flex items-center justify-center px-4 py-8 bg-gray-50">
    <div class="w-full max-w-md">
      <!-- Language Selector -->
      <div class="mb-4 flex justify-end">
        <LanguageSelector />
      </div>

      <!-- Forgot Password Card -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-md p-4">
        <h2 class="text-xl font-semibold text-gray-800 mb-3">{{ t('forgotPassword.title') }}</h2>
        <p class="text-sm text-gray-600 mb-4">{{ t('forgotPassword.subtitle') }}</p>

        <!-- Error Alert -->
        <div v-if="error" class="mb-4 p-3 bg-red-50 border-2 border-red-300 rounded">
          <p class="text-sm text-red-700">{{ error }}</p>
        </div>

        <!-- Success Alert -->
        <div v-if="successMessage" class="mb-4 p-3 bg-green-50 border-2 border-green-300 rounded">
          <p class="text-sm text-green-700">{{ successMessage }}</p>
        </div>

        <form @submit.prevent="handleForgotPassword" class="space-y-3">
          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1">{{ t('forgotPassword.email') }}</label>
            <input
              type="email"
              id="email"
              v-model="email"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              :class="{ 'border-red-500': getFieldError('email') }"
              :placeholder="t('forgotPassword.emailPlaceholder')"
            />
            <p v-if="getFieldError('email')" class="mt-1 text-sm text-red-600">
              {{ getFieldError('email') }}
            </p>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="loading"
            class="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ loading ? t('forgotPassword.sending') : t('forgotPassword.sendButton') }}
          </button>
        </form>

        <!-- Additional Links -->
        <div class="mt-4 pt-3 border-t border-gray-200">
          <div class="flex flex-col gap-2 text-sm">
            <router-link to="/login" class="text-blue-600 hover:text-blue-700">
              {{ t('forgotPassword.backToLogin') }}
            </router-link>
            <p class="text-gray-600">
              {{ t('forgotPassword.noAccount') }}
              <router-link to="/register" class="text-blue-600 hover:text-blue-700 font-medium">
                {{ t('forgotPassword.register') }}
              </router-link>
            </p>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
